import { useCallback, useEffect, useRef } from 'react';
import {
  DWELL_CAP_MS,
  MIN_OBSERVATION_VISIBILITY_RATIO,
  MIN_OBSERVATION_VISIBLE_MS,
  QUICK_SKIP_MAX_DWELL_MS,
} from './viewerSignalDefaults';

const IO_THRESHOLDS = [0, 0.25, 0.5, 0.75, 1];

function newExposureId() {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

/**
 * Tracks real viewport visibility for a piece of content and flushes behavior + impression signals.
 *
 * API:
 *   const { ref, suppress } = useContentVisibilityTelemetry({ enabled, contentType, contentId,
 *     feedSessionId, onFlushBehavior, onFlushImpression });
 *
 * Attach `ref` to the card's root element.
 * Call `suppress()` to flush early (e.g., before hiding).
 *
 * Behavior flush payload:
 *   { exposureId, feedSessionId?, contentType, contentId, visibleDurationMs,
 *     visibilityRatio, peakVisibilityRatio, observationSatisfied, quickSkip, dwellMs }
 *
 * Impression flush payload (subset, backward-compatible with recordImpression):
 *   { impressionId, feedSessionId?, contentType, contentId, visibilityRatio, visibleDurationMs }
 */
export function useContentVisibilityTelemetry({
  enabled,
  contentType,
  contentId,
  feedSessionId,
  onFlushBehavior,
  onFlushImpression,
}) {
  const ref = useRef(null);

  // Stable identity across re-renders — generated once per component mount.
  // In React StrictMode double-mount, the same ref value is preserved, allowing backend dedup.
  const exposureIdRef = useRef(newExposureId());

  // Stable latest-value refs so effect closures never go stale.
  const onFlushBehaviorRef = useRef(onFlushBehavior);
  const onFlushImpressionRef = useRef(onFlushImpression);
  const feedSessionIdRef = useRef(feedSessionId);

  useEffect(() => { onFlushBehaviorRef.current = onFlushBehavior; }, [onFlushBehavior]);
  useEffect(() => { onFlushImpressionRef.current = onFlushImpression; }, [onFlushImpression]);
  useEffect(() => { feedSessionIdRef.current = feedSessionId; }, [feedSessionId]);

  // Guards and tracking — reset at the start of each effect invocation (clean per-mount state).
  const flushedRef = useRef(false);
  const impressionFlushedRef = useRef(false);

  // Accumulated visible milliseconds so far (not including currently-active window).
  const accumulatedMsRef = useRef(0);
  // Timestamp when the current "active accumulation window" started; null when paused.
  const visibleSinceRef = useRef(null);
  const peakRatioRef = useRef(0);
  const currentRatioRef = useRef(0);
  const observationSatisfiedRef = useRef(false);
  const docVisibleRef = useRef(
    typeof document !== 'undefined' ? document.visibilityState === 'visible' : true,
  );
  // Default true; only flip on blur/focus events (document.hasFocus() is unreliable in jsdom/tests).
  const windowFocusedRef = useRef(true);

  /** Monotonic-ish clock; performance.now preferred, Date.now fallback for tests/legacy. */
  function nowMs() {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now();
    }
    return Date.now();
  }

  /** Compute total accumulated ms including any in-progress window. */
  function currentAccumulatedMs() {
    if (visibleSinceRef.current !== null) {
      return accumulatedMsRef.current + Math.max(0, nowMs() - visibleSinceRef.current);
    }
    return accumulatedMsRef.current;
  }

  /** Close the active accumulation window and add elapsed time to the total. */
  function pauseAccumulating() {
    if (visibleSinceRef.current !== null) {
      accumulatedMsRef.current += Math.max(0, nowMs() - visibleSinceRef.current);
      visibleSinceRef.current = null;
    }
  }

  /**
   * Open an accumulation window if conditions are met
   * (intersecting above ratio + document visible + window focused).
   */
  function maybeStartAccumulating() {
    if (
      currentRatioRef.current >= MIN_OBSERVATION_VISIBILITY_RATIO &&
      docVisibleRef.current &&
      windowFocusedRef.current &&
      visibleSinceRef.current === null
    ) {
      visibleSinceRef.current = nowMs();
    }
  }

  const flushBehaviorOnce = useCallback(() => {
    if (flushedRef.current) return;

    pauseAccumulating();

    const dwellMs = Math.min(accumulatedMsRef.current, DWELL_CAP_MS);
    const observationSatisfied = observationSatisfiedRef.current;
    // StrictMode remount / flash unmount: do not ingest empty exposures.
    if (!observationSatisfied && dwellMs <= 0) {
      flushedRef.current = true;
      return;
    }
    flushedRef.current = true;

    // quickSkip only applies when the user actually observed the card then scrolled past quickly.
    const quickSkip = observationSatisfied && dwellMs < QUICK_SKIP_MAX_DWELL_MS;

    onFlushBehaviorRef.current?.({
      exposureId: exposureIdRef.current,
      feedSessionId: feedSessionIdRef.current || undefined,
      contentType,
      contentId,
      visibleDurationMs: dwellMs,
      visibilityRatio: peakRatioRef.current,
      peakVisibilityRatio: peakRatioRef.current,
      observationSatisfied,
      quickSkip,
      dwellMs,
    });
  // contentType and contentId are stable per card mount — intentionally excluded from dep array.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!enabled || !ref.current) return;

    // Reset per-mount state (handles StrictMode double-invocation correctly).
    flushedRef.current = false;
    impressionFlushedRef.current = false;
    accumulatedMsRef.current = 0;
    visibleSinceRef.current = null;
    peakRatioRef.current = 0;
    currentRatioRef.current = 0;
    observationSatisfiedRef.current = false;
    docVisibleRef.current =
      typeof document !== 'undefined' ? document.visibilityState === 'visible' : true;
    windowFocusedRef.current = true;
    // Capture mount-scoped exposure id so cleanup does not read a changing ref.
    const mountExposureId = exposureIdRef.current;

    const el = ref.current;

    function checkAndMaybeSendImpression() {
      if (
        !observationSatisfiedRef.current &&
        currentAccumulatedMs() >= MIN_OBSERVATION_VISIBLE_MS &&
        peakRatioRef.current >= MIN_OBSERVATION_VISIBILITY_RATIO
      ) {
        observationSatisfiedRef.current = true;
        if (!impressionFlushedRef.current) {
          impressionFlushedRef.current = true;
          const accMs = Math.min(accumulatedMsRef.current, DWELL_CAP_MS);
          onFlushImpressionRef.current?.({
            impressionId: mountExposureId,
            feedSessionId: feedSessionIdRef.current || undefined,
            contentType,
            contentId,
            visibilityRatio: peakRatioRef.current,
            visibleDurationMs: accMs,
          });
        }
      }
    }

    const io = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      const ratio = entry.intersectionRatio;
      currentRatioRef.current = ratio;
      if (ratio > peakRatioRef.current) {
        peakRatioRef.current = ratio;
      }

      if (ratio >= MIN_OBSERVATION_VISIBILITY_RATIO && docVisibleRef.current) {
        maybeStartAccumulating();
      } else {
        pauseAccumulating();
        checkAndMaybeSendImpression();
      }
    }, { threshold: IO_THRESHOLDS });

    io.observe(el);

    function handleVisibilityChange() {
      docVisibleRef.current = document.visibilityState === 'visible';
      if (docVisibleRef.current) {
        maybeStartAccumulating();
      } else {
        pauseAccumulating();
        checkAndMaybeSendImpression();
      }
    }

    function handleWindowBlur() {
      windowFocusedRef.current = false;
      pauseAccumulating();
      checkAndMaybeSendImpression();
    }

    function handleWindowFocus() {
      windowFocusedRef.current = true;
      maybeStartAccumulating();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      // Flush impression if satisfied but not yet sent.
      if (observationSatisfiedRef.current && !impressionFlushedRef.current) {
        impressionFlushedRef.current = true;
        const accMs = Math.min(currentAccumulatedMs(), DWELL_CAP_MS);
        onFlushImpressionRef.current?.({
          impressionId: mountExposureId,
          feedSessionId: feedSessionIdRef.current || undefined,
          contentType,
          contentId,
          visibilityRatio: peakRatioRef.current,
          visibleDurationMs: accMs,
        });
      }
      flushBehaviorOnce();
    };
  // enabled, contentType, contentId are stable per card mount.
  // flushBehaviorOnce is stable (empty dep array).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, contentType, contentId]);

  /** Manually flush behavior early, e.g., before the card is hidden. Idempotent. */
  const suppress = useCallback(() => {
    flushBehaviorOnce();
  }, [flushBehaviorOnce]);

  return { ref, suppress };
}
